import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Handshake, Coins, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { affiliateApi, type AffiliateReferral } from '@/api/orgs';
import AffiliateLinksPanel from '@/components/affiliates/AffiliateLinksPanel';
import { formatNIS } from '@/utils/currency';

interface Props {
  org: { id: string; name: string; affiliate_credits_agorot?: number };
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: 'text-amber-400 bg-amber-400/10',
    active: 'text-green-400 bg-green-400/10',
    expired: 'text-gray-400 bg-gray-400/10',
    cancelled: 'text-red-400 bg-red-400/10',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] ?? map.cancelled}`}>
      {status}
    </span>
  );
}

function ProcessCreditModal({
  referral,
  referrerOrgId,
  onClose,
}: {
  referral: AffiliateReferral;
  referrerOrgId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [amountNis, setAmountNis] = useState('');
  const [note, setNote] = useState(
    `עמלה חודשית — ${new Date().toLocaleDateString('he-IL', { month: 'long', year: 'numeric' })}`,
  );

  const mutation = useMutation({
    mutationFn: () => {
      const agorot = Math.round(parseFloat(amountNis) * 100);
      if (Number.isNaN(agorot) || agorot <= 0) throw new Error('סכום לא תקין');
      return affiliateApi.processMonthlyCredit(referral.id, referrerOrgId, agorot, note);
    },
    onSuccess: () => {
      toast.success('קרדיט נרשם בהצלחה');
      queryClient.invalidateQueries({ queryKey: ['affiliate-referrals', referrerOrgId] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-credit-history', referrerOrgId] });
      queryClient.invalidateQueries({ queryKey: ['admin-org', referrerOrgId] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-navy-card border border-navy-border rounded-xl p-6 w-full max-w-md space-y-4">
        <h3 className="text-lg font-semibold text-ink-primary">עבד חודש — {referral.referred_org?.name}</h3>
        <p className="text-sm text-ink-secondary">
          הכנס את סכום העמלה (20% מהתשלום החודשי של הלקוח). הסכום יתווסף לקרדיט של{' '}
          <strong className="text-ink-primary">המפנה</strong>.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-ink-secondary block mb-1">סכום עמלה (₪)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amountNis}
              onChange={(e) => setAmountNis(e.target.value)}
              placeholder="לדוגמה: 49.80"
              className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="text-xs text-ink-secondary block mb-1">הערה</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full bg-navy-bg border border-navy-border rounded-lg px-3 py-2 text-ink-primary text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !amountNis}
            className="flex-1 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg py-2 flex items-center justify-center gap-2 transition-colors"
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            אשר קרדיט
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary border border-navy-border rounded-lg transition-colors"
          >
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OrgAffiliatesTab({ org }: Props) {
  const [selected, setSelected] = useState<AffiliateReferral | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const { data: referrals = [], isLoading } = useQuery({
    queryKey: ['affiliate-referrals', org.id],
    queryFn: () => affiliateApi.getReferrals(org.id),
  });

  const { data: history = [], isLoading: histLoading } = useQuery({
    queryKey: ['affiliate-credit-history', org.id],
    queryFn: () => affiliateApi.getCreditHistory(org.id),
    enabled: showHistory,
  });

  const creditBalance = org.affiliate_credits_agorot ?? 0;

  return (
    <div className="space-y-6">
      <AffiliateLinksPanel referrerOrgId={org.id} referrerOrgName={org.name} />

      <div className="bg-navy-card border border-navy-border rounded-xl p-5 flex items-center gap-4">
        <div className="w-10 h-10 bg-indigo-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
          <Coins className="h-5 w-5 text-indigo-400" />
        </div>
        <div>
          <p className="text-xs text-ink-secondary mb-0.5">יתרת קרדיט JumpStart</p>
          <p className="text-2xl font-bold text-ink-primary font-latin">{formatNIS(creditBalance)}</p>
        </div>
      </div>

      <div className="bg-navy-card border border-navy-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-navy-border flex items-center gap-2">
          <Handshake className="h-4 w-4 text-ink-secondary" />
          <h3 className="text-sm font-semibold text-ink-primary">לקוחות שהופנו</h3>
          <span className="text-xs text-ink-secondary ml-auto">{referrals.length} הפניות</span>
        </div>

        {isLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
          </div>
        ) : referrals.length === 0 ? (
          <div className="py-10 text-center text-ink-secondary text-sm">אין הפניות עדיין</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy-border text-xs text-ink-secondary">
                <th className="text-right px-5 py-2.5 font-medium">ארגון מופנה</th>
                <th className="text-right px-4 py-2.5 font-medium">סטטוס</th>
                <th className="text-right px-4 py-2.5 font-medium">חודשים נותרו</th>
                <th className="text-right px-4 py-2.5 font-medium">{'סה"כ הרווח'}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {referrals.map((r) => (
                <tr key={r.id} className="border-b border-navy-border/50 hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-3">
                    <div className="font-medium text-ink-primary">{r.referred_org?.name ?? '—'}</div>
                    <div className="text-xs text-ink-secondary font-latin">{r.referred_org?.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-3 text-ink-primary font-latin">{r.months_remaining}</td>
                  <td className="px-4 py-3 text-ink-primary font-latin">{formatNIS(r.total_earned_agorot)}</td>
                  <td className="px-4 py-3">
                    {r.status === 'active' && r.months_remaining > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelected(r)}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-md px-3 py-1.5 transition-colors whitespace-nowrap"
                      >
                        עבד חודש
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-navy-card border border-navy-border rounded-xl overflow-hidden">
        <button
          type="button"
          className="w-full px-5 py-4 flex items-center justify-between text-sm text-ink-secondary hover:text-ink-primary transition-colors"
          onClick={() => setShowHistory((h) => !h)}
        >
          <span className="font-medium">היסטוריית קרדיט</span>
          {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {showHistory && (
          <div className="border-t border-navy-border">
            {histLoading ? (
              <div className="py-6 flex justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
              </div>
            ) : history.length === 0 ? (
              <div className="py-6 text-center text-ink-secondary text-sm">אין פעילות עדיין</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-navy-border text-xs text-ink-secondary">
                    <th className="text-right px-5 py-2 font-medium">תאריך</th>
                    <th className="text-right px-4 py-2 font-medium">סוג</th>
                    <th className="text-right px-4 py-2 font-medium">סכום</th>
                    <th className="text-right px-4 py-2 font-medium">הערה</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((tx) => (
                    <tr key={tx.id} className="border-b border-navy-border/50">
                      <td className="px-5 py-2.5 text-ink-secondary font-latin text-xs">
                        {new Date(tx.created_at).toLocaleDateString('he-IL')}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`text-xs font-medium ${
                            tx.type === 'earn'
                              ? 'text-green-400'
                              : tx.type === 'apply'
                                ? 'text-blue-400'
                                : 'text-gray-400'
                          }`}
                        >
                          {tx.type === 'earn'
                            ? 'הרווח'
                            : tx.type === 'apply'
                              ? 'נוצל'
                              : tx.type === 'expire'
                                ? 'פג'
                                : 'ידני'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-latin text-ink-primary">
                        {tx.type === 'earn' || tx.type === 'manual' ? '+' : '-'}
                        {formatNIS(tx.amount_agorot)}
                      </td>
                      <td className="px-4 py-2.5 text-ink-secondary text-xs">{tx.description ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {selected && (
        <ProcessCreditModal referral={selected} referrerOrgId={org.id} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
